import { useMemo } from "react";
import { Link } from "react-router";
import { useGetList, useGetOne, type Identifier } from "ra-core";
import { Download, ExternalLink, Mail, Receipt } from "lucide-react";
import type { Company, Contact } from "@/components/atomic-crm/types";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { resolveInvoiceRecipientEmail } from "@/modules/billing/billingUtils";
import {
  buildClientInvoicePdfContext,
  downloadClientInvoicePdf,
} from "@/modules/billing/clientInvoicePdf";
import { InvoiceDocumentPreview } from "@/modules/billing/InvoiceDocumentPreview";
import {
  resolveInvoiceBalanceDue,
  resolveInvoiceDisplayTotals,
} from "@/modules/billing/invoiceDisplayTotals";
import {
  discountPercentFromAmount,
  lineItemsToInvoiceDrafts,
} from "@/modules/billing/invoiceLineUtils";
import { defaultFeeMethod } from "../../../supabase/functions/_shared/processingFees.ts";
import { buildInvoiceDetailPaymentRows } from "@/modules/billing/invoiceDetailPayments";
import { formatInvoiceMoney } from "@/modules/billing/invoiceEmailTemplate";
import {
  getInvoiceOrganizationBranding,
  resolveInvoiceOrganizationName,
} from "@/modules/billing/invoiceOrganizationInfo";
import type {
  ClientInvoice,
  ClientInvoiceLineItem,
  ClientInvoicePayment,
} from "@/modules/types";

type InvoiceEmailLog = {
  id: Identifier;
  email_type: string;
  recipient_email: string | null;
  sent_at: string | null;
  delivery_status: string | null;
  error_message: string | null;
};

const EMAIL_TYPE_LABELS: Record<string, string> = {
  payment_receipt: "Payment receipt (email)",
  payment_receipt_sms: "Payment receipt (text)",
  payment_reminder: "Payment reminder",
};

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

/**
 * Invoice at a glance from anywhere (e.g. the client timeline): the same
 * document as the invoice page,
 * each payment, and the emails/texts sent for it, with shortcuts to download
 * the PDF or open the full invoice.
 */
export const InvoiceQuickViewDialog = ({
  invoiceId,
  open,
  onOpenChange,
}: {
  invoiceId: Identifier;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { title, companyLegalName } = useConfigurationContext();
  const { data: invoice, isPending } = useGetOne<ClientInvoice>(
    "client_invoices",
    { id: invoiceId },
    { enabled: open },
  );
  const { data: lineItems = [], isPending: linesPending } =
    useGetList<ClientInvoiceLineItem>(
      "client_invoice_line_items",
      {
        filter: { "invoice_id@eq": invoiceId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: "sort_order", order: "ASC" },
      },
      { enabled: open },
    );
  const { data: company } = useGetOne<Company>(
    "companies",
    { id: invoice?.company_id ?? "" },
    { enabled: open && Boolean(invoice?.company_id) },
  );
  const { data: contact } = useGetOne<Contact>(
    "contacts",
    { id: invoice?.contact_id ?? "" },
    { enabled: open && Boolean(invoice?.contact_id) },
  );
  const { data: payments = [] } = useGetList<ClientInvoicePayment>(
    "client_invoice_payments",
    {
      filter: { "invoice_id@eq": invoiceId },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "paid_at", order: "DESC" },
    },
    { enabled: open },
  );
  const { data: emailLogs = [] } = useGetList<InvoiceEmailLog>(
    "client_invoice_email_logs",
    {
      filter: { "invoice_id@eq": invoiceId },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "sent_at", order: "DESC" },
    },
    { enabled: open },
  );

  const organizationName = resolveInvoiceOrganizationName({
    title,
    companyLegalName,
  });
  const branding = useMemo(() => getInvoiceOrganizationBranding(), []);

  const pdfContext = useMemo(() => {
    if (!invoice) return null;
    return buildClientInvoicePdfContext({
      invoice,
      organizationName,
      organizationAddress: branding.address,
      organizationWebsite: branding.website,
      company: company ?? null,
      contact: contact ?? null,
      lineItems,
      billToEmail: resolveInvoiceRecipientEmail({
        company: company ?? null,
        contact: contact ?? null,
        fallbackEmail: invoice.recipient_email,
      }),
    });
  }, [branding, company, contact, invoice, lineItems, organizationName]);

  // Same document and totals as the invoice page.
  const lines = useMemo(() => lineItemsToInvoiceDrafts(lineItems), [lineItems]);
  const feeMethod = invoice
    ? (invoice.fee_method ?? defaultFeeMethod(invoice.payment_method ?? "card"))
    : "card";
  const totals = useMemo(
    () =>
      invoice
        ? resolveInvoiceDisplayTotals(
            invoice,
            lines,
            discountPercentFromAmount(
              Number(invoice.subtotal ?? invoice.amount) || 0,
              Number(invoice.discount_amount) || 0,
            ),
            feeMethod,
          )
        : null,
    [feeMethod, invoice, lines],
  );

  const paymentRows = invoice
    ? buildInvoiceDetailPaymentRows(invoice, [], payments)
    : [];
  const currency = invoice?.currency ?? "USD";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[min(64rem,calc(100vw-2rem))] max-w-none flex-col gap-4 sm:max-w-none">
        <DialogHeader className="flex-row items-start justify-between gap-3 space-y-0 pr-8">
          <div className="min-w-0">
            <DialogTitle className="truncate">
              {invoice?.invoice_number ?? "Invoice"}
            </DialogTitle>
            <DialogDescription>
              {invoice
                ? `${formatInvoiceMoney(Number(invoice.amount) || 0, currency)} · ${formatInvoiceMoney(Number(invoice.amount_paid) || 0, currency)} paid`
                : "Loading invoice…"}
            </DialogDescription>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!pdfContext}
              onClick={() =>
                pdfContext ? void downloadClientInvoicePdf(pdfContext) : null
              }
            >
              <Download className="size-4" />
              PDF
            </Button>
            <Button type="button" size="sm" asChild>
              <Link
                to={`/billing?invoice=${invoiceId}`}
                onClick={() => onOpenChange(false)}
              >
                <ExternalLink className="size-4" />
                Open invoice
              </Link>
            </Button>
          </div>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 gap-4 md:grid-cols-[minmax(0,1fr)_18rem]">
          <div className="min-h-0 overflow-y-auto rounded-lg bg-muted/30 p-2 sm:p-4">
            {invoice && totals && !linesPending ? (
              <InvoiceDocumentPreview
                organizationName={organizationName}
                organizationWebsite={branding.website}
                organizationAddress={branding.address}
                invoiceNumber={invoice.invoice_number ?? undefined}
                status={invoice.status ?? undefined}
                issueDate={invoice.issue_date ?? ""}
                dueDate={invoice.due_date ?? ""}
                terms={invoice.terms ?? "Net 30"}
                company={company ?? null}
                contact={contact ?? null}
                lines={lines}
                termsAndConditions={invoice.notes ?? ""}
                invoice={invoice}
                subtotal={totals.subtotal}
                discountAmount={totals.discountAmount}
                feeAmount={totals.feeAmount}
                total={totals.total}
                balanceDue={resolveInvoiceBalanceDue(invoice, totals)}
              />
            ) : (
              <Skeleton className="h-[60vh] w-full" />
            )}
          </div>

          <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
            <section>
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                <Receipt className="size-3.5" />
                Payments
              </h3>
              {isPending ? (
                <Skeleton className="h-12 w-full" />
              ) : paymentRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No payments yet.
                </p>
              ) : (
                <ul className="space-y-2">
                  {paymentRows.map((row) => (
                    <li
                      key={row.id}
                      className="rounded-md border px-3 py-2 text-sm"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium tabular-nums">
                          {formatInvoiceMoney(row.amount, currency)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {row.dateLabel}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {[
                          row.sourceLabel,
                          row.cardLast4
                            ? `${row.cardBrand ?? "Card"} ••${row.cardLast4}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                <Mail className="size-3.5" />
                Emails & texts sent
              </h3>
              {emailLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {invoice?.sent_at
                    ? `Invoice sent ${formatDateTime(invoice.sent_at)}.`
                    : "Nothing sent yet."}
                </p>
              ) : (
                <ul className="space-y-2">
                  {invoice?.sent_at ? (
                    <li className="rounded-md border px-3 py-2 text-sm">
                      <div className="font-medium">Invoice sent</div>
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(invoice.sent_at)}
                      </div>
                    </li>
                  ) : null}
                  {emailLogs.map((log) => (
                    <li
                      key={String(log.id)}
                      className="rounded-md border px-3 py-2 text-sm"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium">
                          {EMAIL_TYPE_LABELS[log.email_type] ??
                            log.email_type.replace(/_/g, " ")}
                        </span>
                        <span
                          className={
                            log.delivery_status === "failed"
                              ? "text-xs font-medium text-destructive"
                              : "text-xs text-muted-foreground capitalize"
                          }
                        >
                          {log.delivery_status ?? ""}
                        </span>
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {formatDateTime(log.sent_at)}
                        {log.recipient_email ? ` · ${log.recipient_email}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
